import { useRef, useState, type ChangeEvent } from 'react'
import { toast } from 'sonner'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@teach/ui'
import { api } from '@/store/api'
import { useAppDispatch } from '@/store/hooks'
import { idErrorMessage, useChangeAvatarMutation } from '../profileApi'
import { useMessages } from '@/i18n'
import { messages } from './messages'

/** Лимит ID-сервиса на размер файла аватара. */
const MAX_KB = 500

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  current: string | null
}

/** Смена аватарки: превью, выбор файла (JPG или PNG до 500 КБ) и сохранение. */
export const ChangeAvatarDialog = ({ open, onOpenChange, current }: Props) => {
  const dispatch = useAppDispatch()
  const inputRef = useRef<HTMLInputElement>(null)
  const [base64, setBase64] = useState('')
  const [error, setError] = useState('')
  const [save, { isLoading }] = useChangeAvatarMutation()
  const m = useMessages(messages)

  const close = (next: boolean) => {
    if (!next) {
      setBase64('')
      setError('')
    }
    onOpenChange(next)
  }

  const onFile = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget
    const file = input.files?.[0]
    input.value = ''
    if (!file) return
    if (file.size / 1024 >= MAX_KB) {
      setBase64('')
      return setError(m.fileTooBig(MAX_KB))
    }
    setError('')
    const reader = new FileReader()
    reader.onload = () => setBase64(String(reader.result))
    reader.readAsDataURL(file)
  }

  const submit = async () => {
    try {
      await save({ file_base64: base64, is_active: true }).unwrap()
      dispatch(api.util.invalidateTags(['Me']))
      toast.success(m.avatarUpdated)
      close(false)
    } catch (e) {
      setError(idErrorMessage(e, m.avatarUploadFailed))
    }
  }

  const preview = base64 || current

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{m.avatarTitle}</DialogTitle>
          <DialogDescription>{m.avatarHint(MAX_KB)}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3">
          {preview ? (
            <img src={preview} alt="" className="size-32 rounded-2xl object-cover" />
          ) : (
            <div className="size-32 rounded-2xl bg-muted" />
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/png, image/jpeg"
            className="hidden"
            tabIndex={-1}
            aria-hidden="true"
            onChange={onFile}
          />
          <Button variant="secondary" onClick={() => inputRef.current?.click()}>
            {m.chooseFile}
          </Button>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => close(false)}>
            {m.cancel}
          </Button>
          <Button onClick={() => void submit()} disabled={!base64 || isLoading}>
            {m.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
